import { enrichJourneyWithGuided } from '../data/journeys/journeyEngine';

const DEEP_KEYS = [
	'officeLocations', 'officialPdfs', 'weatherByMonth', 'connectivity',
	'gearRentalShops', 'licensedAgencies', 'porterGuide', 'helicopterEvacuation',
	'altitudeSickness', 'emergencyContacts', 'gpxMap', 'offlineDownloads',
	'travelerFaqs', 'trekDaysEnhanced',
];

function hasJourneyContent(apiRow) {
	const sections = apiRow?.sections;
	return sections && typeof sections === 'object' && Object.keys(sections).length > 0
		&& Array.isArray(sections.steps) && sections.steps.length > 0;
}

/** Merge CMS API payload into the shape GuidedJourneyPage expects. */
export function normalizeApiJourney(apiRow) {
	if (!apiRow || !hasJourneyContent(apiRow)) return null;

	const sections = apiRow.sections || {};
	const guidedRaw = apiRow.guided_content || {};
	const deepContent = {};
	DEEP_KEYS.forEach((key) => {
		if (guidedRaw[key] != null) deepContent[key] = guidedRaw[key];
	});

	const guidedMeta = {
		productName: guidedRaw.productName,
		tagline: guidedRaw.tagline,
		preparationTimeline: guidedRaw.preparationTimeline,
		budget: guidedRaw.budget,
		documentsWallet: guidedRaw.documentsWallet,
		packingChecklist: guidedRaw.packingChecklist,
		stepGuided: guidedRaw.stepGuided || {},
	};

	const base = {
		slug: apiRow.slug,
		title: apiRow.title,
		region: apiRow.region,
		coverage: apiRow.coverage_percent,
		handcrafted: apiRow.handcrafted,
		eyebrow: sections.eyebrow,
		subtitle: sections.subtitle || apiRow.highlight,
		lead: sections.lead || apiRow.summary,
		hero: typeof sections.hero === 'string'
			? sections.hero
			: (sections.hero || (apiRow.hero_image_url ? { src: apiRow.hero_image_url, alt: apiRow.title } : undefined)),
		quickFacts: sections.quickFacts,
		faqs: sections.faqs,
		related: sections.related,
		primarySource: sections.primarySource,
		officialSources: sections.officialSources,
		destination: sections.destination,
		district: sections.district || (apiRow.district ? { name: apiRow.district } : undefined),
		lastVerified: sections.lastVerified || apiRow.last_verified_at,
		status: sections.status,
		steps: sections.steps,
		trekDays: sections.trekDays || [],
		booking_defaults: apiRow.booking_defaults || null,
		group_enabled: apiRow.group_enabled,
		private_enabled: apiRow.private_enabled,
		available_months: apiRow.available_months,
		available_months_label: apiRow.available_months_label,
	};

	return enrichJourneyWithGuided(base, guidedMeta, deepContent, {
		coverage: apiRow.coverage_percent,
		status: sections.status,
	});
}

/** Summary row for index cards and booking pickers. */
export function normalizeJourneySummary(apiRow, fullJourney = null) {
	if (fullJourney) {
		return {
			slug: fullJourney.slug,
			title: fullJourney.title,
			region: fullJourney.region,
			subtitle: fullJourney.subtitle,
			lead: fullJourney.lead,
			coverage: fullJourney.coverage,
			quickFacts: fullJourney.quickFacts,
			steps: fullJourney.steps,
			handcrafted: fullJourney.handcrafted,
			hero: fullJourney.hero,
		};
	}

	return {
		slug: apiRow.slug,
		title: apiRow.title,
		region: apiRow.region,
		subtitle: apiRow.highlight || apiRow.summary,
		lead: apiRow.summary || apiRow.highlight,
		coverage: apiRow.coverage_percent || 0,
		handcrafted: apiRow.handcrafted,
		priority: apiRow.priority ?? 0,
		featured: !!apiRow.featured,
		duration_label: apiRow.duration_label,
		difficulty_label: apiRow.difficulty_label,
		group_enabled: apiRow.group_enabled,
		private_enabled: apiRow.private_enabled,
		available_months: apiRow.available_months,
		available_months_label: apiRow.available_months_label,
		booking_defaults: apiRow.booking_defaults || null,
		quickFacts: apiRow.duration_label
			? [{ value: apiRow.duration_label, label: 'Duration' }, { value: apiRow.difficulty_label, label: 'Difficulty' }].filter((f) => f.value)
			: [],
		steps: new Array(16),
	};
}

export function journeySummaryFromStatic(journey, index = 0) {
	const featuredSlugs = new Set(['everest-base-camp', 'annapurna-base-camp', 'langtang-valley']);
	return {
		slug: journey.slug,
		title: journey.title,
		region: journey.region,
		subtitle: journey.subtitle,
		lead: journey.lead,
		coverage: journey.coverage,
		quickFacts: journey.quickFacts,
		steps: journey.steps,
		handcrafted: journey.handcrafted,
		hero: journey.hero,
		priority: (index + 1) * 10,
		featured: featuredSlugs.has(journey.slug),
		duration_label: journey.quickFacts?.[0]?.value,
		difficulty_label: journey.quickFacts?.[1]?.value,
	};
}
